/**
 * Buy Box V5 - Supplement Style with Flavor Selector
 * Gallery, flavor/variant selection, plan selection, and cart logic.
 *
 * Based on the battle-tested V2 JS with flavor selection from V4.
 */
(function() {
  'use strict';

  function init() {
    document.querySelectorAll('.buy-box-v5').forEach(initBuyBox);
  }

  // Robust DOM-ready: works with defer, async, and Shopify section re-renders
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify Theme Editor: re-init when section is reloaded
  document.addEventListener('shopify:section:load', function(e) {
    var section = e.target;
    if (section) {
      var boxes = section.querySelectorAll('.buy-box-v5');
      if (boxes.length > 0) {
        boxes.forEach(initBuyBox);
      }
    }
  });

  function initBuyBox(container) {
    // Prevent double-init
    if (container._bb5Initialized) return;
    container._bb5Initialized = true;

    initGallery(container);
    initFlavorSelection(container);
    initPlanSelection(container);
    initSocialProofScroll(container);
  }

  /* ========== Social Proof Scroll ========== */
  function initSocialProofScroll(container) {
    var socialProof = container.querySelector('.buy-box-v5__social-proof--clickable');
    if (!socialProof) return;

    var reviewsId = socialProof.dataset.reviewsId;
    if (!reviewsId) return;

    socialProof.addEventListener('click', function(e) {
      e.preventDefault();
      var el = document.getElementById(reviewsId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  /* ========== Gallery ========== */
  function initGallery(container) {
    var gallery = container.querySelector('.buy-box-v5__gallery');
    if (!gallery) return;

    var images = gallery.querySelectorAll('.buy-box-v5__gallery-image');
    var thumbnails = gallery.querySelectorAll('.buy-box-v5__thumbnail');
    var prevBtn = gallery.querySelector('.buy-box-v5__gallery-arrow--prev');
    var nextBtn = gallery.querySelector('.buy-box-v5__gallery-arrow--next');
    var counter = gallery.querySelector('.buy-box-v5__gallery-current');

    if (images.length === 0) return;
    var currentIndex = 0;

    function showImage(index) {
      if (index < 0) index = images.length - 1;
      if (index >= images.length) index = 0;
      currentIndex = index;

      images.forEach(function(img, i) {
        img.classList.toggle('buy-box-v5__gallery-image--active', i === currentIndex);
      });
      thumbnails.forEach(function(thumb, i) {
        thumb.classList.toggle('buy-box-v5__thumbnail--active', i === currentIndex);
      });
      if (counter) counter.textContent = currentIndex + 1;
    }

    // Expose for flavor switching
    container._bb5ShowImage = showImage;

    // Arrow navigation
    if (prevBtn) {
      prevBtn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        showImage(currentIndex - 1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        showImage(currentIndex + 1);
      });
    }

    // Thumbnail clicks
    thumbnails.forEach(function(thumb) {
      thumb.addEventListener('click', function(e) {
        e.preventDefault();
        showImage(parseInt(this.dataset.index, 10));
      });
    });

    // Keyboard navigation
    gallery.addEventListener('keydown', function(e) {
      if (e.key === 'ArrowLeft') showImage(currentIndex - 1);
      else if (e.key === 'ArrowRight') showImage(currentIndex + 1);
    });

    // Touch swipe
    var touchStartX = 0;
    var galleryMain = gallery.querySelector('.buy-box-v5__gallery-main');
    if (galleryMain) {
      galleryMain.addEventListener('touchstart', function(e) {
        touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });

      galleryMain.addEventListener('touchend', function(e) {
        var diff = touchStartX - e.changedTouches[0].screenX;
        if (Math.abs(diff) > 50) {
          diff > 0 ? showImage(currentIndex + 1) : showImage(currentIndex - 1);
        }
      }, { passive: true });
    }
  }

  /* ========== Flavor / Variant Selection ========== */
  function initFlavorSelection(container) {
    var flavors = container.querySelectorAll('.buy-box-v5__flavor');
    if (flavors.length === 0) return;

    flavors.forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();

        // Update active state on all flavor buttons
        flavors.forEach(function(f) {
          f.classList.remove('buy-box-v5__flavor--active');
          f.setAttribute('aria-pressed', 'false');
        });
        this.classList.add('buy-box-v5__flavor--active');
        this.setAttribute('aria-pressed', 'true');

        // Switch variant ID on the container
        var variantId = this.dataset.variantId;
        if (variantId && variantId.length > 0) {
          container.dataset.variantId = variantId;
        }

        // Jump gallery to the variant's featured image index
        var imageIndex = parseInt(this.dataset.imageIndex, 10);
        if (!isNaN(imageIndex) && typeof container._bb5ShowImage === 'function') {
          container._bb5ShowImage(imageIndex);
        }
      });
    });
  }

  /* ========== Plan Selection ========== */
  function initPlanSelection(container) {
    var allPlans = container.querySelectorAll('.buy-box-v5__plan');
    var subscriptionPlans = container.querySelectorAll('.buy-box-v5__plan:not(.buy-box-v5__plan--onetime)');
    var onetimePlan = container.querySelector('.buy-box-v5__plan--onetime');
    var perkWithVariants = container.querySelector('.buy-box-v5__perk[data-perk-single]');
    var primaryCta = container.querySelector('.buy-box-v5__cta--primary');

    // Helper: always read fresh from the container
    function getVariantId() {
      return container.dataset.variantId;
    }
    function getSellingPlanId() {
      return container.dataset.sellingPlanId;
    }
    function hasSellingPlan() {
      return container.dataset.hasSellingPlan === 'true';
    }

    // Helper to update primary CTA opacity based on selection
    function updatePrimaryCtaState() {
      if (!primaryCta) return;
      var isOnetimeSelected = onetimePlan && onetimePlan.classList.contains('buy-box-v5__plan--selected');
      if (isOnetimeSelected) {
        primaryCta.classList.add('buy-box-v5__cta--faded');
      } else {
        primaryCta.classList.remove('buy-box-v5__cta--faded');
      }
    }

    // Subscription plan click handlers
    subscriptionPlans.forEach(function(plan) {
      plan.addEventListener('click', function(e) {
        e.preventDefault();

        // Remove selected state from ALL plans
        allPlans.forEach(function(p) {
          p.classList.remove('buy-box-v5__plan--selected');
        });

        // Add selected state to clicked plan
        this.classList.add('buy-box-v5__plan--selected');

        // Update primary CTA state (unfade it)
        updatePrimaryCtaState();

        // Update dynamic perk text
        var planType = this.dataset.plan;
        if (perkWithVariants) {
          var perkText = planType === 'double'
            ? perkWithVariants.dataset.perkDouble
            : perkWithVariants.dataset.perkSingle;
          var textSpan = perkWithVariants.querySelector('span');
          if (textSpan && perkText) {
            textSpan.textContent = perkText;
          }
        }
      });
    });

    // One-time plan click handler — attach to the plan-content div inside the onetime wrapper
    if (onetimePlan) {
      var onetimeClickArea = onetimePlan.querySelector('.buy-box-v5__plan-content');
      (onetimeClickArea || onetimePlan).addEventListener('click', function(e) {
        e.preventDefault();

        // Remove selected state from subscription plans
        subscriptionPlans.forEach(function(p) {
          p.classList.remove('buy-box-v5__plan--selected');
        });

        // Select one-time
        onetimePlan.classList.add('buy-box-v5__plan--selected');

        // Fade the primary CTA
        updatePrimaryCtaState();
      });

      // One-time CTA click — add to cart WITHOUT selling plan
      var onetimeCta = onetimePlan.querySelector('.buy-box-v5__cta--secondary');
      if (onetimeCta) {
        onetimeCta.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();

          var vid = getVariantId();
          if (vid) {
            addToCart(vid, 1, null);
          } else {
            console.error('Buy Box V5: No variant ID found for one-time purchase');
          }
        });
      }
    }

    // Primary CTA click handler — add to cart WITH selling plan
    if (primaryCta) {
      primaryCta.addEventListener('click', function(e) {
        e.preventDefault();

        var isOnetimeSelected = onetimePlan && onetimePlan.classList.contains('buy-box-v5__plan--selected');

        if (isOnetimeSelected) {
          // Switch back to first subscription plan (don't add to cart yet)
          if (subscriptionPlans.length > 0) {
            allPlans.forEach(function(p) {
              p.classList.remove('buy-box-v5__plan--selected');
            });
            subscriptionPlans[0].classList.add('buy-box-v5__plan--selected');
            updatePrimaryCtaState();
          }
          return;
        }

        // Get the currently selected subscription plan
        var selectedSubscription = container.querySelector('.buy-box-v5__plan--selected:not(.buy-box-v5__plan--onetime)');

        if (selectedSubscription) {
          var quantity = parseInt(selectedSubscription.dataset.quantity, 10) || 1;
          var vid = getVariantId();

          if (vid) {
            if (hasSellingPlan() && getSellingPlanId()) {
              addToCart(vid, quantity, getSellingPlanId());
            } else {
              console.warn('Buy Box V5: No selling plan found — adding without subscription');
              addToCart(vid, quantity, null);
            }
          } else {
            console.error('Buy Box V5: No variant ID found');
          }
        } else {
          console.warn('Buy Box V5: No subscription plan selected');
        }
      });
    }
  }

  /* ========== Add to Cart ========== */
  function addToCart(variantId, quantity, sellingPlanId) {
    if (!variantId) {
      console.error('Buy Box V5: No variant ID provided');
      return;
    }

    var item = {
      id: parseInt(variantId, 10),
      quantity: quantity
    };

    if (sellingPlanId) {
      item.selling_plan = parseInt(sellingPlanId, 10);
    }

    // Show loading state on ALL V5 CTAs
    var ctaButtons = document.querySelectorAll('.buy-box-v5 .buy-box-v5__cta');
    ctaButtons.forEach(function(btn) {
      btn.classList.add('buy-box-v5__cta--loading');
      btn.disabled = true;
    });

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ items: [item] })
    })
    .then(function(response) {
      if (!response.ok) {
        return response.json().then(function(err) {
          throw new Error(err.message || err.description || 'Failed to add to cart');
        });
      }
      return response.json();
    })
    .then(function(data) {
      console.log('Buy Box V5: Added to cart', data);

      // Reset loading state
      ctaButtons.forEach(function(btn) {
        btn.classList.remove('buy-box-v5__cta--loading');
        btn.disabled = false;
      });

      // Open cart drawer
      openCartDrawer();
    })
    .catch(function(error) {
      console.error('Buy Box V5: Error adding to cart', error);

      // Reset loading state
      ctaButtons.forEach(function(btn) {
        btn.classList.remove('buy-box-v5__cta--loading');
        btn.disabled = false;
      });

      alert('Error: ' + error.message);
    });
  }

  /* ========== Open Cart Drawer ========== */
  function openCartDrawer() {
    // Pattern 1: Shopify standard events
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('cart:open'));
    document.dispatchEvent(new CustomEvent('theme:cart:open'));

    // Pattern 2: Click cart icon triggers
    var cartTriggers = [
      '[data-cart-trigger]',
      '.js-cart-trigger',
      '.cart-icon',
      '[data-action="open-drawer"][data-drawer="cart"]',
      '#cart-icon',
      '.header__icon--cart',
      '[href="/cart"]'
    ];

    var drawerOpened = false;

    for (var i = 0; i < cartTriggers.length; i++) {
      var trigger = document.querySelector(cartTriggers[i]);
      if (trigger && typeof trigger.click === 'function') {
        var isDrawerTrigger =
          trigger.hasAttribute('data-cart-trigger') ||
          trigger.hasAttribute('data-action') ||
          trigger.classList.contains('js-cart-trigger');

        if (isDrawerTrigger) {
          trigger.click();
          drawerOpened = true;
          break;
        }
      }
    }

    // Pattern 3: Add open class to known drawer elements
    var drawerSelectors = [
      '.cart-drawer', '#cart-drawer', '.drawer--cart',
      '[data-drawer="cart"]', '.mini-cart', '.cart-flyout'
    ];

    for (var j = 0; j < drawerSelectors.length; j++) {
      var drawer = document.querySelector(drawerSelectors[j]);
      if (drawer) {
        drawer.classList.add('is-open', 'active', 'open');
        drawerOpened = true;

        var overlay = document.querySelector('.drawer-overlay, .cart-overlay');
        if (overlay) {
          overlay.classList.add('is-open', 'active');
        }
        break;
      }
    }

    // Fallback: redirect to /cart
    if (!drawerOpened) {
      setTimeout(function() {
        var visible = document.querySelector(
          '.cart-drawer.is-open, .drawer--cart.active, [data-drawer="cart"].open'
        );
        if (!visible) {
          window.location.href = '/cart';
        }
      }, 300);
    }
  }

})();
